import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { PRODUCTS, COMPANY } from "../data/site";
import { ArrowLeft, ArrowRight, Phone, MessageCircle, Download } from "lucide-react";

const Products = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const id = setInterval(() => emblaApi && emblaApi.scrollNext(), 4800);
    return () => clearInterval(id);
  }, [emblaApi]);

  return (
    <section
      id="products"
      className="relative py-28 bg-gradient-to-b from-[#fff5e9] via-[#fffaf3] to-[#fff5e9] overflow-hidden"
      data-testid="products-section"
    >
      {/* spotlights */}
      <div className="spotlight w-[400px] h-[400px] bg-orange-400 top-0 left-1/2 -translate-x-1/2 opacity-25" />

      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-[10px] font-extrabold tracking-[0.25em] uppercase font-mono mb-5">
              Featured Range
            </div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#1a0a05] leading-[0.95] tracking-tight">
              Our <span className="text-heat brush">Product</span><br />
              Range.
            </h2>
            <p className="text-[#3a2418] mt-5 text-base max-w-xl">
              Engineered, manufactured and quality-tested in-house — discover our complete catalogue of industrial heaters &amp; heating elements.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={COMPANY.catalogueUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="products-catalogue-btn"
              className="inline-flex items-center gap-2 bg-[#1a0a05] text-amber-50 px-5 py-3 rounded-full font-bold hover:bg-[#3a1106] transition-colors text-sm"
            >
              <Download className="w-4 h-4" /> Catalogue PDF
            </a>
            <button
              onClick={scrollPrev}
              data-testid="products-prev"
              aria-label="Previous"
              className="w-12 h-12 rounded-full bg-white border border-orange-200 text-[#1a0a05] grid place-items-center hover:bg-orange-50 transition-colors lift"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <button
              onClick={scrollNext}
              data-testid="products-next"
              aria-label="Next"
              className="w-12 h-12 rounded-full heat-gradient text-white grid place-items-center shadow-xl shadow-red-500/30 hover:scale-105 transition-transform"
            >
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div className="overflow-hidden -mx-3" ref={emblaRef} data-testid="products-carousel">
          <div className="flex">
            {PRODUCTS.map((p, i) => (
              <div
                key={p.name}
                className="shrink-0 grow-0 basis-[88%] sm:basis-[60%] md:basis-[42%] lg:basis-[32%] xl:basis-[26%] px-3"
              >
                <article
                  data-testid={`product-slide-${i}`}
                  className="product-card group bg-white rounded-3xl overflow-hidden border border-orange-100 h-full flex flex-col"
                >
                  <div className="relative aspect-[5/4] overflow-hidden bg-orange-50">
                    <img
                      src={p.img}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1200ms]"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute top-3 left-3 flex items-center gap-2">
                      <span className="bg-white/90 backdrop-blur px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-[0.18em] text-orange-700 uppercase font-mono">
                        {String(i + 1).padStart(2, "0")} / {String(PRODUCTS.length).padStart(2, "0")}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-display font-extrabold text-[#1a0a05] leading-tight text-xl mb-3 tracking-tight">
                      {p.name}
                    </h3>
                    <p className="text-[#3a2418]/75 text-sm leading-relaxed flex-1">{p.desc}</p>
                    <div className="mt-6 pt-5 border-t border-orange-100/70 flex gap-2">
                      <a
                        href={`tel:${COMPANY.contacts[0].phone}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 heat-gradient text-white text-xs font-bold px-3 py-2.5 rounded-full hover:scale-[1.02] transition-transform"
                        data-testid={`product-call-${i}`}
                      >
                        <Phone className="w-3.5 h-3.5" /> Call
                      </a>
                      <a
                        href={`https://wa.me/${COMPANY.contacts[0].whatsapp}?text=${encodeURIComponent(`Hello, I'd like a quote for ${p.name}.`)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 inline-flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-bold px-3 py-2.5 rounded-full"
                        data-testid={`product-quote-${i}`}
                      >
                        <MessageCircle className="w-3.5 h-3.5" /> Quote
                      </a>
                    </div>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-1.5 mt-10" data-testid="products-dots">
          {scrollSnaps.map((_, i) => (
            <button
              key={i}
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
              data-testid={`products-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === selectedIndex ? "w-10 heat-gradient" : "w-1.5 bg-orange-200 hover:bg-orange-300"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Products;
